import React, { useState, useEffect } from "react";
import FlowHeader from "../../../components/FlowHeader";
import { useNavigate } from "react-router-dom";
import { usePurchase } from "../../../context/PurchaseContext";
import { useCart } from "../../../hooks/useCart";

const StepProgress = ({ categoryId }) => {
  // Kategori bazlı step başlığı
  const getStepTitle = () => {
    switch (categoryId) {
      case "1": return "Halı Detayları";
      case "2": return "Koltuk Detayları";
      case "3": return "Perde Detayları";
      case "4": return "Yorgan Detayları";
      case "5": return "Temizlik Detayları";
      default: return "Ürün Detayları";
    }
  };
  
  return (
    <div className="w-[427px]">
      <div className="flex items-center">
        <div className="flex items-center relative flex-1">
          {/* İlk Nokta */}
          <div className="flex flex-col items-center">
            <div className="w-4 h-4 bg-[#4F46E5] rounded-full z-10"></div>
            <span className="text-xs mt-2 text-[#4F46E5] font-medium absolute -bottom-6 whitespace-nowrap">
              {getStepTitle()}
            </span>
          </div>
          {/* İlk Çizgi */}
          <div className="flex-1 h-[2px] bg-[#4F46E5] ml-0"></div>
        </div>

        <div className="flex items-center relative flex-1">
          {/* İkinci Nokta */}
          <div className="flex flex-col items-center">
            <div className="w-4 h-4 border-2 border-[#4F46E5] rounded-full bg-white z-10"></div>
            <span className="text-xs mt-2 text-gray-400 font-medium absolute -bottom-6 whitespace-nowrap">
              Adres Bilgileri
            </span>
          </div>
          {/* İkinci Çizgi */}
          <div className="flex-1 h-[2px] bg-gray-200"></div>
        </div>

        <div className="flex items-center relative">
          {/* Son Nokta */}
          <div className="flex flex-col items-center">
            <div className="w-4 h-4 border-2 border-gray-200 rounded-full bg-white z-10"></div>
            <span className="text-xs mt-2 text-gray-400 font-medium absolute -bottom-6 whitespace-nowrap">
              Ödeme
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const CarpetDetails = () => {
  const navigate = useNavigate();
  const { purchaseData, updateCarpetTypes, updateDealerId, updateEstimatedDeliveryDate, updateProductDetails } = usePurchase();
  const { cartItems } = useCart();
  
  // Her ürün için detayları sakla
  const [productDetails, setProductDetails] = useState({});
  const [errors, setErrors] = useState({});
  
  // Ürün isimlerine göre kategori tahmin et
  const getCategoryFromProductName = (productName) => {
    const name = productName.toLowerCase();
    if (name.includes('halı') || name.includes('kilim')) return "1";
    if (name.includes('koltuk') || name.includes('baza') || name.includes('sandalye')) return "2";
    if (name.includes('perde')) return "3";
    if (name.includes('yorgan') || name.includes('battaniye')) return "4";
    if (name.includes('ev temizliği') || name.includes('temizlik')) return "5";
    return "1"; // Varsayılan halı yıkama
  };
  
  // Tüm bayilerin isimlerini al
  const dealerNames = [...new Set(cartItems.map(item => item.dealer_name))].join(', ') || "Bayi";
  const dealerIds = [...new Set(cartItems.map(item => item.dealer_id))];
  
  // Kategori bazlı başlık ve placeholder belirle
  const getCategoryTexts = (categoryId) => {
    switch (categoryId) {
      case "1":
        return {
          title: "Halı Türleri",
          placeholder: "Halı Tipi",
          fieldName: "halı tipi"
        };
      case "2":
        return {
          title: "Koltuk Türleri",
          placeholder: "Koltuk Tipi",
          fieldName: "koltuk tipi"
        };
      case "3":
        return {
          title: "Perde Türleri",
          placeholder: "Perde Tipi",
          fieldName: "perde tipi"
        };
      case "4":
        return {
          title: "Yorgan Türleri",
          placeholder: "Yorgan Tipi",
          fieldName: "yorgan tipi"
        };
      case "5":
        return {
          title: "Temizlik Detayları",
          placeholder: "Temizlik Detayı",
          fieldName: "temizlik detayı"
        };
      default:
        return {
          title: "Ürün Detayları",
          placeholder: "Ürün Detayı",
          fieldName: "ürün detayı"
        };
    }
  };
  
  // Tahmini teslimat tarihini hesapla (3 gün sonra)
  const getEstimatedDeliveryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 3);
    
    const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    
    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return {
      formatted: `${day} ${month} ${year} - ${dayName}`,
      iso: date.toISOString()
    };
  };
  
  const estimatedDelivery = getEstimatedDeliveryDate();
  
  useEffect(() => {
    // Her ürün için boş detay alanları oluştur
    const initialDetails = {};
    cartItems.forEach(item => {
      const itemKey = `${item.dealer_id}_${item.product_id}`;
      initialDetails[itemKey] = {
        productName: item.product_name,
        dealerName: item.dealer_name,
        categoryId: item.category_id || getCategoryFromProductName(item.product_name),
        details: ["", "", "", ""] // 4 detay alanı
      };
    });
    setProductDetails(initialDetails);
    
    // Bayi ID'lerini kaydet
    if (dealerIds.length > 0) {
      updateDealerId(dealerIds[0]); // İlk bayi ID'si
    }
    
    // Tahmini teslimat tarihini kaydet
    updateEstimatedDeliveryDate(estimatedDelivery.iso);
  }, [cartItems]);
  
  const handleProductDetailChange = (productKey, index, value) => {
    setProductDetails(prev => ({
      ...prev,
      [productKey]: {
        ...prev[productKey],
        details: prev[productKey].details.map((detail, i) => i === index ? value : detail)
      }
    }));
    
    // Hata varsa temizle
    if (errors[productKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[productKey];
        return newErrors;
      });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Her ürün için en az bir detay girilmiş olmalı
    Object.entries(productDetails).forEach(([key, product]) => {
      const filledDetails = product.details.filter(detail => detail.trim() !== "");
      if (filledDetails.length === 0) {
        const categoryTexts = getCategoryTexts(product.categoryId);
        newErrors[key] = `${product.productName} için en az bir ${categoryTexts.fieldName} girmelisiniz.`;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleNext = () => {
    if (!validateForm()) {
      return;
    }
    
    // Tüm ürün detaylarını hazırla
    const allProductDetails = {};
    Object.entries(productDetails).forEach(([key, product]) => {
      const filledDetails = product.details.filter(detail => detail.trim() !== "");
      if (filledDetails.length > 0) {
        allProductDetails[key] = {
          ...product,
          details: filledDetails
        };
      }
    });
    
    // İlk ürünün detaylarını carpetTypes olarak kaydet (backward compatibility)
    const firstProductKey = Object.keys(productDetails)[0];
    if (firstProductKey && allProductDetails[firstProductKey]) {
      updateCarpetTypes(allProductDetails[firstProductKey].details);
    }
    
    // Tüm ürün detaylarını kaydet
    if (updateProductDetails) {
      updateProductDetails(allProductDetails);
    }
    
    navigate("/purchase/carpet-cleaning/address-details");
  };

  return (
    <div className="min-h-screen bg-white">
      <FlowHeader />

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex justify-center">
          <div className="w-[427px]">
            <StepProgress categoryId="1" />

            {/* Satıcı ve Teslimat Bilgileri */}
            <div className="mt-[58px] space-y-2.5 mb-8">
              <div className="inline-flex items-center border border-black rounded-full py-2 px-4">
                <span className="text-sm">
                  <span className="font-medium">Satıcı / Sağlayıcı : </span>
                  {dealerNames}
                </span>
              </div>
              <div className="block">
                <div className="inline-flex items-center border border-black rounded-full py-2 px-4">
                  <span className="text-sm">
                    <span className="font-medium">Tahmini Teslim : </span>
                    {estimatedDelivery.formatted}
                  </span>
                </div>
              </div>
              <div className="block">
                <div className="inline-flex items-center border border-black rounded-full py-2 px-4">
                  <span className="text-sm">
                    <span className="font-medium">Ürün Sayısı : </span>
                    {cartItems.length}
                  </span>
                </div>
              </div>
            </div>

            {/* Ayırıcı Çizgi */}
            <div className="mb-8">
              <div className="w-[424px] h-[0.5px] bg-black opacity-50"></div>
            </div>

            {/* Ürün Detayları Formu */}
            <div>
              <h2 className="text-xl font-semibold mb-6">
                Ürün Detayları
              </h2>
              
              {/* Her ürün için ayrı detay alanları */}
              {Object.entries(productDetails).map(([productKey, product], productIndex) => {
                const categoryTexts = getCategoryTexts(product.categoryId);
                return (
                  <div key={productKey} className="mb-8">
                    {/* Ürün başlığı */}
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium">
                        {productIndex + 1}. {product.productName}
                      </h3>
                      <span className="text-sm text-gray-500">
                        {product.dealerName}
                      </span>
                    </div>
                    
                    {/* Hata mesajı */}
                    {errors[productKey] && (
                      <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600">{errors[productKey]}</p>
                      </div>
                    )}
                    
                    {/* Detay alanları */}
                    <div className="space-y-3 pl-4">
                      {product.details.map((detail, index) => (
                        <div key={index} className="relative">
                          <input
                            type="text"
                            value={detail}
                            onChange={(e) => handleProductDetailChange(productKey, index, e.target.value)}
                            placeholder={`${index + 1}.${categoryTexts.placeholder}`}
                            className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent ${
                              errors[productKey] ? 'border-red-300' : 'border-gray-200'
                            }`}
                          />
                        </div>
                      ))}
                    </div>
                    
                    <p className="mt-2 text-sm text-gray-500 pl-4">
                      En az bir {categoryTexts.fieldName} girmeniz gerekmektedir.
                    </p>
                    
                    {/* Ürünler arası ayırıcı */}
                    {productIndex < Object.keys(productDetails).length - 1 && (
                      <div className="mt-6 border-b border-gray-200"></div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Devam Et Butonu */}
            <div className="mt-8">
              <button
                className="w-full bg-[#4F46E5] text-white py-4 rounded-lg hover:bg-[#4338CA] transition-colors"
                onClick={handleNext}
              >
                Devam Et
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarpetDetails;
